import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  StyledLeagueSwitcher,
  StyledLeagueTrigger,
  StyledLeagueTriggerName,
  StyledLeagueChevron,
  StyledLeagueOverlay,
  StyledLeagueMenu,
  StyledLeagueMenuLabel,
  StyledLeagueOption,
  StyledLeagueCrest,
  StyledLeagueOptionName,
  StyledLeagueCheck,
  StyledLeagueNum,
} from './styled';
import {
  leagueDivisionFromName,
  stripLeagueDecor,
} from '../../service/leagueTable';
import { placeAnchoredMenu, subscribeViewport } from '../../service/placeAnchoredMenu';

const Chevron = () => (
  <svg width="10" height="6" viewBox="0 0 10 6" aria-hidden="true">
    <path
      d="M1 1l4 4 4-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Check = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
    <path
      d="M2.5 7.2l3 3.2 6-6.8"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Crest = ({ src, name, compact }) => (
  <StyledLeagueCrest $compact={compact}>
    {src ? <img src={src} alt="" /> : <span>{(name || '?').slice(0, 1)}</span>}
  </StyledLeagueCrest>
);

const LeagueMark = ({ league, compact }) => {
  const division = leagueDivisionFromName(league?.name);
  if (division) {
    return (
      <StyledLeagueNum $compact={compact} aria-hidden="true">
        {division}
      </StyledLeagueNum>
    );
  }
  return (
    <Crest src={league?.symbol} name={stripLeagueDecor(league?.name)} compact={compact} />
  );
};

export default function LeagueSwitcher({
  leagues,
  value,
  onChange,
  disabled,
}) {
  const triggerRef = useRef(null);
  const closingRef = useRef(false);
  const openedAtRef = useRef(0);
  const [rendered, setRendered] = useState(false);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({});

  const selected =
    (leagues || []).find((league) => league._id === value) || null;
  const selectedName = stripLeagueDecor(selected?.name) || 'Select a league';

  const place = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    setCoords(placeAnchoredMenu(trigger, { align: 'end', maxWidth: 280 }));
  }, []);

  const closeMenu = useCallback(() => {
    closingRef.current = true;
    setOpen(false);
  }, []);

  const openMenu = useCallback(() => {
    if (disabled) return;
    closingRef.current = false;
    openedAtRef.current = Date.now();
    place();
    setRendered(true);
  }, [disabled, place]);

  useLayoutEffect(() => {
    if (!rendered || closingRef.current) return undefined;
    let inner;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setOpen(true));
    });
    return () => {
      cancelAnimationFrame(outer);
      if (inner) cancelAnimationFrame(inner);
    };
  }, [rendered]);

  useEffect(() => {
    if (!rendered) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') closeMenu();
    };
    const onReposition = () => place();
    window.addEventListener('keydown', onKey);
    const stopViewport = subscribeViewport(onReposition);
    return () => {
      window.removeEventListener('keydown', onKey);
      stopViewport();
    };
  }, [rendered, closeMenu, place]);

  useEffect(() => {
    if (open || !rendered || !closingRef.current) return undefined;
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const id = window.setTimeout(() => setRendered(false), reduced ? 0 : 220);
    return () => window.clearTimeout(id);
  }, [open, rendered]);

  const onOverlayClick = () => {
    if (Date.now() - openedAtRef.current < 400) return;
    closeMenu();
  };

  const onMenuTransitionEnd = (event) => {
    if (event.propertyName !== 'opacity') return;
    if (event.currentTarget.getAttribute('data-open') !== 'true') {
      setRendered(false);
    }
  };

  const pick = (id) => {
    closeMenu();
    if (id !== value) onChange(id);
  };

  if (!leagues || !leagues.length) return null;

  return (
    <StyledLeagueSwitcher>
      <StyledLeagueTrigger
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Select a league"
        onClick={() => (open ? closeMenu() : openMenu())}
      >
        <LeagueMark league={selected} compact />
        <StyledLeagueTriggerName>{selectedName}</StyledLeagueTriggerName>
        <StyledLeagueChevron data-open={open ? 'true' : 'false'}>
          <Chevron />
        </StyledLeagueChevron>
      </StyledLeagueTrigger>
      {rendered &&
        createPortal(
          <>
            <StyledLeagueOverlay $open={open} onClick={onOverlayClick} />
            <StyledLeagueMenu
              role="listbox"
              aria-label="Leagues"
              data-open={open ? 'true' : 'false'}
              style={coords}
              onTransitionEnd={onMenuTransitionEnd}
            >
              <StyledLeagueMenuLabel>Leagues</StyledLeagueMenuLabel>
              {leagues.map((league) => {
                const active = league._id === value;
                return (
                  <StyledLeagueOption
                    key={league._id}
                    type="button"
                    role="option"
                    aria-selected={active}
                    $active={active}
                    onClick={() => pick(league._id)}
                  >
                    <LeagueMark league={league} />
                    <StyledLeagueOptionName>
                      {stripLeagueDecor(league.name) || league.name}
                    </StyledLeagueOptionName>
                    {active ? (
                      <StyledLeagueCheck>
                        <Check />
                      </StyledLeagueCheck>
                    ) : null}
                  </StyledLeagueOption>
                );
              })}
            </StyledLeagueMenu>
          </>,
          document.body
        )}
    </StyledLeagueSwitcher>
  );
}
